import { ListType } from 'constants/enums'
import { IVideoPlaylist } from 'components/video/playlist/video-playlist'
import { Article } from 'components/main/article/article'
import { IListItems } from 'components/main/list/list-interface'
import {
    DrivingModelsParam,
    DrivingSponsoredVehiclesParam,
    DrivingVehiclesParam,
    IDriving,
    IDrivingMake,
    IDrivingModel,
    IDrivingVehicle
} from 'components/driving/driving-interface'
import { ComponentBase } from 'utils/abstract-classes/component-base'
import { Nullable } from 'constants/types'
import { logger } from 'utils/system/logger'

export class ApiService extends ComponentBase {
    public DEFAULT_MODEL_TEMPLATE = 'vehicle-card-list'
    public async fetchList(
        id: string,
        categoryName: string,
        categoryType: string,
        categoryFrom = 0
    ): Promise<IListItems> {
        let url = ''
        switch (categoryType) {
            case ListType.GLOBE_NEWSWIRE:
                url = '/api-root/content_external/globe_newswire/'
                break
            case ListType.RSS:
                url = id
                break
            default:
                url = `/api-root/lists/${id}/expanded/?format=json&name=${categoryName}&from=${categoryFrom}&type=${categoryType}`
        }
        return fetch(url)
            .then(async (res) => {
                return res.json()
            })
            .catch((err) => logger('error', err))
    }

    public async fetchListHtml(
        id: string,
        categorySlug: string,
        categoryType: string,
        categoryFrom = 0,
        originUrls?: boolean,
        isSponsored?: boolean,
        template?: string
    ): Promise<Array<Article> | void> {
        let url = ''
        switch (categoryType) {
            case ListType.TRENDING:
                url =
                    '/api-root/audience/trending_articles_by_market/?limit=10&format=html'
                break
            case ListType.RELATED_ARTICLES:
                url = `/api-root/audience/related_stories/${id}/?limit=5&format=html`
                break
            case ListType.RSS:
            case ListType.PARTNER_FEED:
                url =
                    id +
                    (id.indexOf('?') > -1 ? '&' : '?') +
                    `format=html&load_origin_urls=${originUrls}&is_sponsored=${isSponsored}`
                break
            default:
                url = `/api-root/lists/${id}/expanded/?format=html&name=${categorySlug}&from=${categoryFrom}&type=${categoryType}&load_origin_urls=${originUrls}&is_sponsored=${isSponsored}`
        }
        if (template) {
            url += `&template_name=${template}`
        }
        return fetch(url)
            .then(async (res) => {
                return res.text()
            })
            .then((html: string) => {
                // Convert the HTML string into a document object
                const parser = new DOMParser()
                const doc = parser.parseFromString(html, 'text/html')

                const articles: Article[] = []

                // Extract all of the articles
                const articleElements = this.getElements('article', doc)

                articleElements.forEach((articleElement) => {
                    articles.push(new Article(articleElement))
                })

                return articles
            })
            .catch((err) => logger('error', err))
    }

    public async fetchVideoPlaylist(
        playlistId: string
    ): Promise<IVideoPlaylist> {
        const url = `/api-root/media/videos/playlists/${playlistId}/player/json/`
        return fetch(url)
            .then(async (res) => res.json())
            .catch((err) => logger('error', err))
    }

    public async fetchWeatherForRegionAndCity(
        region: string,
        city: string
    ): Promise<string | Nullable<HTMLElement> | void> {
        const url = `/api-root/weather/current_conditions/${region}/${city}/?format=html`
        const elClass = '.weather-widget__main-area'
        return fetch(url)
            .then(async (res) => {
                return res.text()
            })
            .then((html: string) => {
                // Convert the HTML string into a document object
                const parser = new DOMParser()
                const doc = parser.parseFromString(html, 'text/html')

                // Extract data
                const data = this.getElement(elClass, doc)
                return data
            })
            .catch((err) => logger('error', err))
    }

    public async fetchDrivingMake(
        year = 0,
        page = 1
    ): Promise<IDriving<IDrivingMake>> {
        let params = ''
        if (year !== 0) params += `&year=${year}`
        if (page > 1) {
            if (params !== '') params += '&'
            params += `page=${page}`
        }
        const url = `/api-root/driving/makes/?${params}`
        return fetch(url)
            .then(async (res) => res.json())
            .catch((err) => logger('error', err))
    }

    /**
     * Retrieve Driving models
     *
     * @template {IDriving<IDrivingModel> | Response}
     * @param {DrivingModelsParam} attrs
     * @returns {Promise<T>}
     */
    public async fetchDrivingModels<
        T extends IDriving<IDrivingModel> | Response
    >(attrs: DrivingModelsParam): Promise<T> {
        const params = new URLSearchParams()
        if (attrs.make) params.set('make', attrs.make)
        if (attrs.bodystyle) params.set('bodystyle', attrs.bodystyle)
        if (attrs.year) params.set('year', attrs.year)
        if (attrs.price_min) params.set('min_msrp', attrs.price_min.toString())
        if (attrs.price_max) params.set('max_msrp', attrs.price_max.toString())
        if (attrs.seats) params.set('number_of_seats', attrs.seats.toString())
        if (attrs.power_source)
            params.set('power_source', attrs.power_source.toString())
        if (attrs.transmission)
            params.set('transmission', attrs.transmission.toString())
        if (attrs.page) params.set('page', attrs.page.toString())
        if (attrs.page_size) params.set('page_size', attrs.page_size.toString())
        if (attrs.ordering) params.set('ordering', attrs.ordering.toString())
        if (attrs.use_latest_year)
            params.set('use_latest_year', attrs.use_latest_year.toString())
        if (attrs.format) params.set('format', attrs.format)
        if (attrs.template_name)
            params.set(
                'template_name',
                attrs.template_name || this.DEFAULT_MODEL_TEMPLATE
            )

        const url = ['/api-root/driving/models/', params.toString()]
        return fetch(url.join('?'))
            .then(async (res) => {
                if (attrs.format !== 'html') {
                    return res.json()
                }
                return res
            })
            .catch((err) => logger('error', err))
    }

    /**
     * Retrieve Driving vehicles
     *
     * @template {IDriving<IDrivingVehicle> | Response}
     * @param {DrivingVehiclesParam} attrs
     * @returns {Promise<T>}
     */
    public async fetchDrivingVehicles<
        T extends IDriving<IDrivingVehicle> | Response
    >(attrs: DrivingVehiclesParam): Promise<T> {
        const params = new URLSearchParams()
        if (attrs.make) params.set('make', attrs.make)
        if (attrs.model) params.set('model', attrs.model)
        if (attrs.bodystyle) params.set('bodystyle', attrs.bodystyle)
        if (attrs.year) params.set('year', attrs.year)
        if (attrs.page) params.set('page', attrs.page.toString())
        if (attrs.page_size) params.set('page_size', attrs.page_size.toString())
        if (attrs.format) params.set('format', attrs.format)

        const url = ['/api-root/driving/vehicles/', params.toString()]
        return fetch(url.join('?'))
            .then(async (res) => {
                if (attrs.format !== 'html') {
                    return res.json()
                }
                return res
            })
            .catch((err) => console.error(err))
    }

    /**
     * Retrieve Driving vehicle
     *
     * @param {number} id
     * @return {Promise<IDrivingVehicle>}
     */
    public async fetchDrivingVehicle(id: number): Promise<IDrivingVehicle> {
        return fetch(`/api-root/driving/vehicles/${id}`)
            .then(async (res) => res.json())
            .catch((err) => logger('error', err))
    }

    /**
     * Retrieve sponsored vehicle
     *
     * @param {number[]} ids
     * @return {Promise<IDrivingVehicle>}
     */
    public async fetchDrivingSponsoredVehicle(
        attrs: DrivingSponsoredVehiclesParam
    ): Promise<IDrivingVehicle> {
        const params = new URLSearchParams()
        if (attrs.vehicle_ids)
            params.set('vehicle_ids', attrs.vehicle_ids.join(','))
        if (attrs.bodystyles)
            params.set('bodystyle', attrs.bodystyles.join(','))
        if (attrs.msrps) params.set('msrp', attrs.msrps.join(','))

        const url = [
            '/api-root/driving/sponsored-car-comparison/',
            params.toString()
        ]
        return fetch(url.join('?'))
            .then(async (res) => res.json())
            .catch((err) => logger('error', err))
    }
}
